// src/features/timetracking/export/ExportTTModal.tsx
import * as React from 'react';
import { fetchTimeTrackings, toExportRows, exportCSV, exportXLSX, exportPDF, ExportOptions, FetchParams } from './ttExport';
import { colors, shadows } from '../../employees/styles/personalakteStyles';

type Props = {
  open: boolean;
  onClose: () => void;

  // Kontexte aus TimeTrackings.tsx:
  currentRange: { from: string; to: string };
  currentWorkAreaIds: number[];           // Multi-Select WA-Filter
  activeEmployeeId?: number | null;       // optional
  activeEmployeeName?: string | null;     // optional, nur kosmetisch

  tenantTimeZone?: string;                // optional
  employeeNameById?: (id:number|string)=>string|undefined; // optional
};

export function ExportTTModal({
  open, onClose, currentRange, currentWorkAreaIds,
  activeEmployeeId, activeEmployeeName,
  tenantTimeZone, employeeNameById
}: Props){
  const [format, setFormat] = React.useState<'csv'|'xlsx'|'pdf'>('xlsx');
  const [scope, setScope] = React.useState<'current'|'active'>('current'); // "current" = alle TTs im aktuellen Filter; "active" = nur aktiver MA
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const canUseActive = !!activeEmployeeId;

  React.useEffect(()=>{
    if (open) { setError(null); setBusy(false); setScope(canUseActive ? 'active' : 'current'); setFormat('xlsx'); }
  }, [open, canUseActive]);

  async function onExport(){
    try{
      setBusy(true); setError(null);
      const params: FetchParams = {
        start_date: currentRange.from,
        end_date: currentRange.to,
        work_area_ids: currentWorkAreaIds,
      };
      // Mitarbeiter-Filter auch in "current" berücksichtigen, falls vorhanden
      if ((scope==='active' && activeEmployeeId) || (scope==='current' && activeEmployeeId)) {
        params.employee = activeEmployeeId;
      }

      const records = await fetchTimeTrackings(params);
      const rows = toExportRows(records, { tenantTimeZone, employeeNameById });

      const base = makeFileBaseName(scope, activeEmployeeId, activeEmployeeName, currentRange);
      const opt: ExportOptions = {
        tenantTimeZone, employeeNameById,
        fileBaseName: base,
        excelIncludeSummarySheet: true,
        pdfTitle: 'Time Trackings',
      };

      if (format==='csv')      exportCSV(rows, opt);
      else if (format==='xlsx') await exportXLSX(rows, opt);
      else if (format==='pdf')  await exportPDF(rows, opt);

      onClose();
    } catch(e:any){
      setError(e?.message ?? 'Export fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  const optionStyle = (selected: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
    fontSize: 13, fontWeight: selected ? 600 : 400,
    color: selected ? colors.navy : colors.textBody,
    background: selected ? '#fff' : 'transparent',
    boxShadow: selected ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
    border: selected ? `1px solid ${colors.border}` : '1px solid transparent',
    transition: 'all 0.15s',
  });

  return (
    <div
      className="glass-modal-backdrop"
      style={{
        position:'fixed', inset: 0,
        display:'flex', alignItems:'center', justifyContent:'center',
        zIndex: 1000,
      }}
      onClick={()=> !busy && onClose()}
    >
      <div
        style={{
          width: 420, maxWidth:'92vw', padding: '24px',
          position:'relative', animation: 'fadeIn 0.15s ease-out',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.04)',
        }}
        onClick={(e)=>e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: colors.navy }}>
              Export Time Trackings
            </h3>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
              {currentRange.from} – {currentRange.to}
            </div>
          </div>
          <button
            aria-label="Close" onClick={onClose} disabled={busy}
            style={{
              border:'none', background:'transparent',
              fontSize: 18, cursor:'pointer', color: colors.textLight,
              width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 8, flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = colors.bgHover; e.currentTarget.style.color = colors.textBody; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = colors.textLight; }}
          >×</button>
        </div>

        <div style={{ display:'flex', flexDirection: 'column', gap: 16 }}>

          {/* Format */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Format</div>
            <div style={{ display: 'flex', gap: 6, background: colors.bgMuted, borderRadius: 10, padding: 4 }}>
              {([['xlsx', 'Excel'], ['csv', 'CSV'], ['pdf', 'PDF']] as const).map(([val, lbl]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setFormat(val)}
                  style={{
                    flex: 1, padding: '7px 0', borderRadius: 7, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: format === val ? 600 : 400,
                    background: format === val ? '#fff' : 'transparent',
                    color: format === val ? colors.navy : colors.textMuted,
                    boxShadow: format === val ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.15s',
                  }}
                >
                  {lbl}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: colors.textLight, marginTop: 6 }}>
              Große Zeiträume: CSV/Excel. Kompakte Umfänge: PDF.
            </div>
          </div>

          {/* Scope */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Scope</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, background: colors.bgMuted, borderRadius: 10, padding: 4 }}>
              {canUseActive && (
                <label style={optionStyle(scope === 'active')}>
                  <input type="radio" name="scope" checked={scope==='active'} onChange={()=>setScope('active')} style={{ display: 'none' }}/>
                  <span style={{
                    width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                    border: scope === 'active' ? `5px solid ${colors.slate}` : `2px solid ${colors.border}`,
                    background: '#fff', boxSizing: 'border-box',
                  }} />
                  Nur aktiver MA{activeEmployeeName ? `: ${activeEmployeeName}` : ''}
                </label>
              )}
              <label style={optionStyle(scope === 'current')}>
                <input type="radio" name="scope" checked={scope==='current'} onChange={()=>setScope('current')} style={{ display: 'none' }}/>
                <span style={{
                  width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                  border: scope === 'current' ? `5px solid ${colors.slate}` : `2px solid ${colors.border}`,
                  background: '#fff', boxSizing: 'border-box',
                }} />
                Alle Einträge im aktuellen Filter
              </label>
            </div>
          </div>

          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 8,
              background: colors.dangerLight, color: colors.danger,
              fontSize: 12, fontWeight: 500,
            }} role="alert">
              {error}
            </div>
          )}

          {/* Actions */}
          <div style={{ display:'flex', gap: 8, marginTop: 4 }}>
            <button
              onClick={onClose} disabled={busy}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 10,
                border: `1px solid ${colors.border}`, background: '#fff',
                color: colors.textMuted, fontSize: 13, fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Abbrechen
            </button>
            <button
              onClick={onExport} disabled={busy}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 10,
                border: 'none', background: colors.navy,
                color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', opacity: busy ? 0.7 : 1,
              }}
            >
              {busy ? 'Export läuft…' : 'Export starten'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function makeFileBaseName(
  scope:'current'|'active',
  activeEmployeeId: number | null | undefined,
  activeEmployeeName: string | null | undefined,
  range: { from:string; to:string }
){
  const emp = (scope==='active' && activeEmployeeId) ? (activeEmployeeName ? `${slug(activeEmployeeName)}_${activeEmployeeId}` : `employee_${activeEmployeeId}`) : 'current-filter';
  return `tt_${emp}_${range.from}_${range.to}`;
}
function slug(s:string){ return s.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-_]/g,'').slice(0,60); }
